import tw, { styled } from 'twin.macro';
import Image from 'next/image';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';
import { animated, useSpring } from '@react-spring/web';
import { useTranslation } from 'next-i18next';

import { customImageLoader, getAssetSrc, useResponsiveBreakPoints } from '@restworld/utils-common';
import { LinkBase } from '@restworld/ui-ds';

import { useStaticImage } from '../../context/static-images';

export type JobDomainInterestCount = {
  domain: 'reception' | 'cucina' | 'bar' | 'sala';
  label: string;
  count: number;
};

export type JobPositionsPerDomainProps = {
  redirectToPath: (path: string, query?: { [key: string]: string | boolean }) => void;
  jobsPerDomains: JobDomainInterestCount[];
};

export const JobPositionsPerDomain = ({ redirectToPath, jobsPerDomains }: JobPositionsPerDomainProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const [overlayHeight, setOverlayHeight] = useState<number>(0);
  const { isDesktop } = useResponsiveBreakPoints();
  const domainsImg = useStaticImage('domains-desktop', getAssetSrc('/utils-assets/domains.png'));
  const domainsSmallImg = useStaticImage('domains-small', getAssetSrc('/utils-assets/domains-small.png'));

  useEffect(() => {
    // Get the height of the second container's content
    if (overlayRef.current) {
      const height = overlayRef.current.scrollHeight + (isDesktop ? 0 : 0);
      setOverlayHeight(height);
    }
  }, [isDesktop]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.style.height = `${overlayHeight}px`;
    }
  }, [isDesktop, overlayHeight]);

  const { t: homePageTranslate } = useTranslation('home-page');
  const { t: commonTranslate } = useTranslation('common');
  return (
    <Wrapper>
      <Container ref={containerRef}>
        <StyledImage
          {...(isDesktop ? domainsImg : domainsSmallImg)}
          fill={true}
          alt="Domains"
          loader={customImageLoader}
        />
        <ContainerOverlay ref={overlayRef}>
          <div tw="lg:w-[23.813rem]  w-full lg:px-0 px-2.25 ">
            <Header>{homePageTranslate('work_without_success')}</Header>
            <Title>{homePageTranslate('find_opportunity_on_most_searched')}</Title>
          </div>
          <CardsContainer>
            {jobsPerDomains.map(({ domain, count, label }, index) => (
              <JobJobPositionsPerDomainCard
                {...{
                  image: getDomainImage(domain),
                  openPositions: count,
                  title: `${commonTranslate('positions')} in ${label}`,
                  redirectUrl: `/cerco-lavoro/${domain.toLowerCase()}`,
                }}
                key={index}
              />
            ))}
          </CardsContainer>
        </ContainerOverlay>
      </Container>
    </Wrapper>
  );
};

function getDomainImage(domain: JobDomainInterestCount['domain']) {
  switch (domain) {
    case 'bar':
      return getAssetSrc('/utils-assets/bar-domain.svg');
    case 'reception':
      return getAssetSrc('/utils-assets/hotel-domain.svg');
    case 'sala':
      return getAssetSrc('/utils-assets/rooms-domain.svg');
    case 'cucina':
      return getAssetSrc('/utils-assets/chef-domain.svg');
    default:
      return undefined;
  }
}

type JobJobPositionsPerDomainCardProps = {
  image: string;
  openPositions: number;
  title: string;
  actionText?: string;
  redirectUrl: string;
};

export const JobJobPositionsPerDomainCard = ({
  image,
  openPositions,
  title,
  actionText,
  redirectUrl,
}: JobJobPositionsPerDomainCardProps) => {
  const { positionsCount } = useSpring({
    positionsCount: openPositions,
    from: { positionsCount: 0 },
  });
  const { t: commonTranslate } = useTranslation('common');
  return (
    <CardWrapper>
      <div tw="flex lg:flex-col flex-row  lg:gap-1 gap-2 mb-1">
        <Image src={image} width={32} height={32} alt={title} loader={customImageLoader} />
        <CardHeader>{positionsCount.to((positions) => Math.floor(positions))}</CardHeader>
      </div>
      <CardTitle>{title}</CardTitle>
      <div tw="mt-4">
        <LinkBase link={redirectUrl}>
          <ActionText>
            <span tw="text-base leading-4 text-primary-800 ">{actionText ?? commonTranslate('discover_them_all')}</span>
            <ArrowRightIcon className="h-5 w-5 font-medium text-primary-700" />
          </ActionText>
        </LinkBase>
      </div>
    </CardWrapper>
  );
};

const Wrapper = styled.div(() => [tw`flex flex-col w-full items-center lg:pt-28 pt-16.5 lg:pb-16 pb-20`]);

const Container = styled.div(() => [
  tw`relative flex flex-col lg:!w-[57.625rem] lg:!min-h-[36.5rem] lg:!h-[36.5rem] !w-[327px]  h-[1016px]  lg:rounded-7.5 rounded-3xl overflow-hidden`,
]);

const ContainerOverlay = styled.div(() => [
  tw`absolute z-20 items-start top-0 left-0 flex flex-col w-full box-border lg:px-12 lg:pb-12 md:pb-6  lg:pt-17.75 pb-6 pt-9 px-6 lg:bg-domainLg bg-domainSm`,
]);

const Header = styled.h3(() => [tw`lg:text-5xl text-4xl font-semibold leading-12.75 text-white`]);

const Title = styled.h5(() => [tw`lg:text-lg text-base font-medium leading-6.75 text-white mt-4`]);

const StyledImage = styled(Image)(() => [tw` w-full`]);

const CardsContainer = styled.div(() => [tw`grid lg:grid-cols-4 grid-cols-1 gap-4 w-full  lg:mt-12.25 mt-31`]);

const CardWrapper = styled.div(() => [tw`flex flex-col items-start p-6 bg-white rounded-3.75 `]);

const CardHeader = styled(animated.h4)(() => [tw`text-4xl font-semibold text-black`]);

const CardTitle = styled.p(() => [tw`text-sm leading-5.25 text-black`]);

const ActionText = styled.div(() => [tw`inline-flex text-base text-left gap-1.5 hover:cursor-pointer`]);
